import { useTranslation } from 'next-i18next/pages';

import type { IconSizeType } from '@/types/icon';

import { IconArrowUpRightFromSquareRound } from '../../ui/icon';

/**
 * ### ExternalLinkMarkProps
 *
 * Sizing for the external-link mark.
 */
export interface ExternalLinkMarkProps {
  /**
   * ### size
   *
   * Icon edge length in pixels; pick the step closest to the link's font size.
   *
   * @defaultValue `16` — matches 16px body text
   */
  size?: IconSizeType;
}

/**
 * ### ExternalLinkMark
 *
 * Marks a link that opens in a new window: the arrow-out icon for sighted users
 * and a visually hidden notice for assistive technology, so both hear the same
 * warning before leaving the site.
 * Render it inside the anchor, after the label, so the notice joins the link's name.
 *
 * @param props Icon size
 * @returns The icon and its hidden notice
 * @see {@link ExternalLinkMarkProps} Default applied to the omitted size
 */
export function ExternalLinkMark({ size = 16 }: ExternalLinkMarkProps) {
  const { t } = useTranslation('common');

  return (
    <>
      <IconArrowUpRightFromSquareRound size={size} aria-hidden="true" />
      <span className="sr-only">{t('새 창에서 열림')}</span>
    </>
  );
}
